import {useMemo, useState} from 'react'
import {Card, Spinner} from 'react-bootstrap'
import {useMutation, useQuery} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {Link} from 'react-router-dom'
import {ModalCreateApplication} from './components/ModalCreateApplication'
import {ModalCreateAgentUser} from './components/ModalCreateAgentUser'
import {UserAvatar} from '../../components/UserAvatar'
import Select from 'react-select'
import {US_STATES} from '../../helpers/catalogs'
import {GET_AGENT_ONBOARDING_APPLICATIONS} from '../../gql/queries/agentOnboardingQueries'
import {UPDATE_AGENT_ONBOARDING_REVIEW_STATUS} from '../../gql/mutations/agentOnboardingMutations'
import {
  AppointmentStatus,
  IAgentOnboardingApplication,
  ReviewStatus,
} from '../../types/agentOnboarding'
import {useGetUser} from '../../store/userStore'
import {hasPermission} from '../../helpers'
import {ALL_PERMISSIONS} from '../../helpers/consts'

const M = defineMessages({
  title: {id: 'onboarding.list.title', defaultMessage: 'Agent onboarding'},
  subtitle: {
    id: 'onboarding.list.subtitle',
    defaultMessage: 'Review and manage applications submitted through the onboarding portal.',
  },
  search: {id: 'onboarding.list.search', defaultMessage: 'Search by name or email…'},
  allStatuses: {id: 'onboarding.list.allStatuses', defaultMessage: 'All statuses'},
  allStates: {id: 'onboarding.list.allStates', defaultMessage: 'All states'},
  statTotal: {id: 'onboarding.list.stat.total', defaultMessage: 'Total'},
  statPending: {id: 'onboarding.list.stat.pending', defaultMessage: 'Pending'},
  statApproved: {id: 'onboarding.list.stat.approved', defaultMessage: 'Approved'},
  statRejected: {id: 'onboarding.list.stat.rejected', defaultMessage: 'Rejected'},
  subWithDocuments: {
    id: 'onboarding.list.stat.subWithDocuments',
    defaultMessage: '{count} with documents',
  },
  subShareOfTotal: {
    id: 'onboarding.list.stat.subShareOfTotal',
    defaultMessage: '{percent}% of the total',
  },
  statTotalCaption: {
    id: 'onboarding.list.stat.totalCaption',
    defaultMessage: 'All applications received',
  },
  statPendingCaption: {
    id: 'onboarding.list.stat.pendingCaption',
    defaultMessage: 'Awaiting contracting review',
  },
  statApprovedCaption: {
    id: 'onboarding.list.stat.approvedCaption',
    defaultMessage: 'Cleared to move forward',
  },
  statRejectedCaption: {
    id: 'onboarding.list.stat.rejectedCaption',
    defaultMessage: 'Not moving forward',
  },
  statusSubmitted: {id: 'onboarding.status.submitted', defaultMessage: 'Submitted'},
  statusInProgress: {id: 'onboarding.status.inProgress', defaultMessage: 'In progress'},
  reviewPending: {id: 'onboarding.review.PENDING', defaultMessage: 'Pending'},
  reviewReviewing: {id: 'onboarding.review.REVIEWING', defaultMessage: 'Reviewing'},
  reviewApproved: {id: 'onboarding.review.APPROVED', defaultMessage: 'Approved'},
  reviewRejected: {id: 'onboarding.review.REJECTED', defaultMessage: 'Rejected'},
  reviewActivated: {id: 'onboarding.review.ACTIVATED', defaultMessage: 'Activated'},
  statusUpdated: {id: 'onboarding.toast.statusUpdated', defaultMessage: 'Review status updated'},
  statusFailed: {
    id: 'onboarding.toast.statusFailed',
    defaultMessage: 'Could not update the review status',
  },
  licensed: {id: 'onboarding.badge.licensed', defaultMessage: 'Licensed'},
  notLicensed: {id: 'onboarding.badge.notLicensed', defaultMessage: 'Not licensed'},
  licenseInProcess: {id: 'onboarding.badge.licenseInProcess', defaultMessage: 'License in process'},
  documents: {id: 'onboarding.badge.documents', defaultMessage: '{count} docs'},
  upline: {id: 'onboarding.field.upline', defaultMessage: 'Upline'},
  noUpline: {id: 'onboarding.field.noUpline', defaultMessage: 'No upline'},
  step: {id: 'onboarding.field.step', defaultMessage: 'Step'},
  reference: {id: 'onboarding.field.reference', defaultMessage: 'Reference'},
  empty: {id: 'onboarding.list.empty', defaultMessage: 'No applications yet.'},
  emptyFiltered: {
    id: 'onboarding.list.emptyFiltered',
    defaultMessage: 'No applications match these filters.',
  },
  loadError: {id: 'onboarding.list.loadError', defaultMessage: 'Could not load applications.'},
  addAgent: {id: 'onboarding.list.addAgent', defaultMessage: 'Add agent'},
  createUser: {id: 'onboarding.list.createUser', defaultMessage: 'Create user'},
  thAgent: {id: 'onboarding.list.th.agent', defaultMessage: 'Agent'},
  thCarriers: {id: 'onboarding.list.th.carriers', defaultMessage: 'Carriers'},
  thLicense: {id: 'onboarding.list.th.license', defaultMessage: 'License & documents'},
  thApplication: {id: 'onboarding.list.th.application', defaultMessage: 'Application'},
  thReview: {id: 'onboarding.list.th.review', defaultMessage: 'Review status'},
  thUser: {id: 'onboarding.list.th.user', defaultMessage: 'System user'},
  appointmentCount: {
    id: 'onboarding.list.appointmentCount',
    defaultMessage: '{count} {status}',
  },
  apptSubmitted: {id: 'onboarding.carriers.SUBMITTED', defaultMessage: 'Submitted'},
  apptReviewing: {id: 'onboarding.carriers.REVIEWING', defaultMessage: 'Reviewing'},
  apptContracted: {id: 'onboarding.carriers.CONTRACTED', defaultMessage: 'Contracted'},
  apptRejected: {id: 'onboarding.carriers.REJECTED', defaultMessage: 'Rejected'},
})

const dash = '—'
// Bare symbols in JSX trip formatjs/no-literal-string-in-jsx; build the composed strings in
// expressions instead of leaving punctuation loose in the markup.
const separator = ' · '

const APPOINTMENT_ORDER: AppointmentStatus[] = [
  'CONTRACTED',
  'REVIEWING',
  'SUBMITTED',
  'REJECTED',
]

const APPOINTMENT_TONE: Record<AppointmentStatus, string> = {
  NOT_STARTED: 'dark',
  SUBMITTED: 'info',
  REVIEWING: 'warning',
  CONTRACTED: 'success',
  REJECTED: 'danger',
}

const REVIEW_STATUSES: ReviewStatus[] = [
  'PENDING',
  'REVIEWING',
  'APPROVED',
  'REJECTED',
  'ACTIVATED',
]

/** Semantic colours per the panel spec: amber pending, green approved, red rejected. */
const REVIEW_TONE: Record<ReviewStatus, string> = {
  PENDING: 'warning',
  REVIEWING: 'info',
  APPROVED: 'success',
  REJECTED: 'danger',
  ACTIVATED: 'primary',
}

/**
 * Reuses the `card-summary` tones from _tfi.scss (the same ones PoliciesPage uses) instead of a
 * neutral grey card: the coloured surface is what makes the four figures readable at a glance.
 */
function StatCard({
  label,
  caption,
  value,
  sub,
  tone,
}: {
  label: string
  caption: string
  value: number
  sub: string
  tone: 'blue' | 'green' | 'pending' | 'canceled'
}) {
  return (
    <Card className={`card-summary ${tone} flex-fill mx-2 text-center`}>
      <h3>{label}</h3>
      <Card.Title>{caption}</Card.Title>
      <Card.Body>
        {value}
        <div className='card-summary-sub'>{sub}</div>
      </Card.Body>
    </Card>
  )
}

export const OnboardingPage = () => {
  const intl = useIntl()
  const {data, loading, error, refetch} = useQuery<{
    agentOnboardingApplications: IAgentOnboardingApplication[]
  }>(GET_AGENT_ONBOARDING_APPLICATIONS, {fetchPolicy: 'cache-and-network'})

  const [showCreate, setShowCreate] = useState(false)
  const [userFor, setUserFor] = useState<IAgentOnboardingApplication | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | ReviewStatus>('all')
  // Geographic US state — deliberately a separate name from statusFilter, they are easy to mix up.
  const [stateFilter, setStateFilter] = useState('all')

  const [updateReviewStatus, {loading: updating}] = useMutation(
    UPDATE_AGENT_ONBOARDING_REVIEW_STATUS
  )
  const permissions = useGetUser((s) => s.user?.permissions) ?? []
  const canChangeStatus = hasPermission(permissions, ALL_PERMISSIONS.CHANGE_AGENT_ONBOARDING)

  const reviewLabel = (status: ReviewStatus) =>
    intl.formatMessage(
      {
        PENDING: M.reviewPending,
        REVIEWING: M.reviewReviewing,
        APPROVED: M.reviewApproved,
        REJECTED: M.reviewRejected,
        ACTIVATED: M.reviewActivated,
      }[status]
    )

  const handleStatusChange = async (applicationId: string, reviewStatus: ReviewStatus) => {
    try {
      const res = await updateReviewStatus({variables: {applicationId, reviewStatus}})
      const payload = res.data?.updateAgentOnboardingReviewStatus
      if (!payload?.success) {
        // Backend copy is shown verbatim: it names the exact rejection.
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.statusFailed))
        return
      }
      toast.success(intl.formatMessage(M.statusUpdated))
      await refetch()
    } catch {
      toast.error(intl.formatMessage(M.statusFailed))
    }
  }

  const applications = useMemo(() => data?.agentOnboardingApplications ?? [], [data])

  // Solo los estados presentes en los datos, pero con su nombre: "FL" no dice nada por sí solo.
  const stateOptions = useMemo(() => {
    const codes = Array.from(
      new Set(applications.map((a) => a.usState).filter((s): s is string => !!s))
    )
    return codes
      .map((code) => ({
        value: code,
        label: US_STATES.find((s) => s.value === code)?.label ?? code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [applications])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return applications.filter((app) => {
      if (statusFilter !== 'all' && app.reviewStatus !== statusFilter) return false
      if (stateFilter !== 'all' && app.usState !== stateFilter) return false
      if (!term) return true
      return (
        (app.fullName ?? '').toLowerCase().includes(term) ||
        (app.email ?? '').toLowerCase().includes(term)
      )
    })
  }, [applications, search, statusFilter, stateFilter])

  // Counters reflect the real data set, not the filtered view.
  const stats = useMemo(
    () => ({
      total: applications.length,
      pending: applications.filter((a) => a.reviewStatus === 'PENDING').length,
      approved: applications.filter((a) => a.reviewStatus === 'APPROVED').length,
      rejected: applications.filter((a) => a.reviewStatus === 'REJECTED').length,
      withDocuments: applications.filter((a) => a.documentsCount > 0).length,
    }),
    [applications]
  )

  const appointmentLabel = (status: AppointmentStatus) =>
    intl.formatMessage(
      {
        NOT_STARTED: M.apptSubmitted,
        SUBMITTED: M.apptSubmitted,
        REVIEWING: M.apptReviewing,
        CONTRACTED: M.apptContracted,
        REJECTED: M.apptRejected,
      }[status]
    )

  const share = (count: number) =>
    stats.total === 0 ? 0 : Math.round((count / stats.total) * 100)

  /** Cuántas aseguradoras hay en cada estado. "No iniciado" se omite: es la ausencia de fila. */
  const appointmentSummary = (app: IAgentOnboardingApplication) => {
    const counts = new Map<AppointmentStatus, number>()
    app.carrierAppointments.forEach((a) => counts.set(a.status, (counts.get(a.status) ?? 0) + 1))
    return APPOINTMENT_ORDER.filter((status) => counts.get(status)).map((status) => ({
      status,
      count: counts.get(status) as number,
    }))
  }

  const statusOptions = [
    {value: 'all', label: intl.formatMessage(M.allStatuses)},
    ...REVIEW_STATUSES.map((s) => ({value: s as string, label: reviewLabel(s)})),
  ]

  const stateFilterOptions = [
    {value: 'all', label: intl.formatMessage(M.allStates)},
    ...stateOptions,
  ]

  const licenseBadge = (app: IAgentOnboardingApplication) => {
    if (app.licenseStatus === 'inProcess') {
      return {label: intl.formatMessage(M.licenseInProcess), tone: 'warning'}
    }
    return app.hasLicense
      ? {label: intl.formatMessage(M.licensed), tone: 'success'}
      : {label: intl.formatMessage(M.notLicensed), tone: 'dark'}
  }

  if (loading && !data) {
    return (
      <Card>
        <Card.Body className='d-flex align-items-center justify-content-center' style={{height: '40vh'}}>
          <Spinner animation='border' />
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-6'>
        <div className='d-flex flex-column'>
          <h1 className='fw-bold text-gray-900 mb-1'>{intl.formatMessage(M.title)}</h1>
          <span className='text-gray-600'>{intl.formatMessage(M.subtitle)}</span>
        </div>
        {canChangeStatus && (
          <button type='button' className='btn btn-primary' onClick={() => setShowCreate(true)}>
            <i className='bi bi-plus-lg me-1' />
            {intl.formatMessage(M.addAgent)}
          </button>
        )}
      </div>

      <ModalCreateApplication
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          refetch()
        }}
      />

      <ModalCreateAgentUser
        application={userFor}
        onClose={() => setUserFor(null)}
        onCreated={() => {
          refetch()
        }}
      />

      {error && <div className='alert alert-danger'>{intl.formatMessage(M.loadError)}</div>}

      <div className='policies-summary d-flex justify-content-between mb-6'>
        <StatCard
          tone='blue'
          label={intl.formatMessage(M.statTotal)}
          caption={intl.formatMessage(M.statTotalCaption)}
          value={stats.total}
          sub={intl.formatMessage(M.subWithDocuments, {count: stats.withDocuments})}
        />
        <StatCard
          tone='pending'
          label={intl.formatMessage(M.statPending)}
          caption={intl.formatMessage(M.statPendingCaption)}
          value={stats.pending}
          sub={intl.formatMessage(M.subShareOfTotal, {percent: share(stats.pending)})}
        />
        <StatCard
          tone='green'
          label={intl.formatMessage(M.statApproved)}
          caption={intl.formatMessage(M.statApprovedCaption)}
          value={stats.approved}
          sub={intl.formatMessage(M.subShareOfTotal, {percent: share(stats.approved)})}
        />
        <StatCard
          tone='canceled'
          label={intl.formatMessage(M.statRejected)}
          caption={intl.formatMessage(M.statRejectedCaption)}
          value={stats.rejected}
          sub={intl.formatMessage(M.subShareOfTotal, {percent: share(stats.rejected)})}
        />
      </div>

      <Card className='mb-6'>
        <Card.Body className='row g-4'>
          <div className='col-md-6'>
            <div className='position-relative'>
              <i className='bi bi-search position-absolute top-50 translate-middle-y ms-4 text-gray-500' />
              <input
                type='text'
                className='form-control form-control-solid ps-12'
                placeholder={intl.formatMessage(M.search)}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className='col-md-3'>
            <Select
              value={statusOptions.find((o) => o.value === statusFilter) ?? statusOptions[0]}
              options={statusOptions}
              menuPortalTarget={document.body}
              styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
              onChange={(opt) =>
                setStatusFilter((opt as {value: string} | null)?.value as 'all' | ReviewStatus)
              }
            />
          </div>
          <div className='col-md-3'>
            <Select
              value={stateFilterOptions.find((o) => o.value === stateFilter) ?? stateFilterOptions[0]}
              options={stateFilterOptions}
              menuPortalTarget={document.body}
              styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
              onChange={(opt) => setStateFilter((opt as {value: string} | null)?.value ?? 'all')}
            />
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className='pt-2'>
          {filtered.length === 0 ? (
            <div className='text-center text-gray-500 py-10'>
              {applications.length === 0
                ? intl.formatMessage(M.empty)
                : intl.formatMessage(M.emptyFiltered)}
            </div>
          ) : (
            <div className='table-responsive table table-striped table-hover'>
              <table
                className='table-scrollable table-scroll table table-striped table-hover'
                style={{whiteSpace: 'nowrap'}}
              >
                <thead>
                  <tr role='row'>
                    {[M.thAgent, M.thCarriers, M.thLicense, M.thApplication, M.thReview, M.thUser].map(
                      (header) => (
                        <th key={header.id} className='fw-bold'>
                          <span style={{fontSize: '0.85em', lineHeight: '1.2'}}>
                            {intl.formatMessage(header)}
                          </span>
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((app) => {
                    const license = licenseBadge(app)
                    const progressLabel = app.submitted
                      ? intl.formatMessage(M.statusSubmitted)
                      : [intl.formatMessage(M.statusInProgress), app.currentStep ?? dash].join(
                          separator
                        )
                    return (
                      <tr key={app.id}>
                        {/* 1 — identidad: avatar del upline, nombre, correo y de quién cuelga */}
                        <td>
                          <div className='d-flex align-items-center gap-4'>
                            <UserAvatar
                              user={app.upline}
                              size={45}
                              tooltipId={`upline-${app.id}`}
                            />
                            <div className='min-w-0'>
                              <Link
                                to={`/onboarding/${app.id}`}
                                className='fw-bold text-gray-900 fs-6 text-hover-primary'
                              >
                                {app.fullName || dash}
                              </Link>
                              <div className='text-gray-500 fs-7'>{app.email || dash}</div>
                              <div className='text-gray-400 fs-8'>
                                {[app.usState || dash, app.organizationName || dash].join(
                                  separator
                                )}
                              </div>
                              <div className='d-flex align-items-center gap-2 mt-1'>
                                <i className='bi bi-diagram-3 fs-8 text-gray-500' />
                                {app.uplineName ? (
                                  <span className='badge badge-light-primary fs-8'>
                                    {app.uplineName}
                                  </span>
                                ) : (
                                  <span className='badge badge-light-warning fs-8'>
                                    {intl.formatMessage(M.noUpline)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* 2 — contrataciones, apiladas: en una fila se desbordaban */}
                        <td>
                          {app.carrierAppointments.length === 0 ? (
                            <span className='text-gray-400 fs-8'>{dash}</span>
                          ) : (
                            <div className='d-flex flex-column align-items-start gap-1'>
                              {appointmentSummary(app).map(({status, count}) => (
                                <span
                                  key={status}
                                  className={`badge badge-light-${APPOINTMENT_TONE[status]} fs-8`}
                                >
                                  {intl.formatMessage(M.appointmentCount, {
                                    count,
                                    status: appointmentLabel(status),
                                  })}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>

                        {/* 3 — licencia y documentos */}
                        <td>
                          <div className='d-flex flex-column align-items-start gap-1'>
                            <span className={`badge badge-light-${license.tone} fs-8`}>
                              {license.label}
                            </span>
                            <span className='badge badge-light-info fs-8'>
                              <i className='bi bi-file-earmark-text me-1' />
                              {intl.formatMessage(M.documents, {count: app.documentsCount})}
                            </span>
                          </div>
                        </td>

                        {/* 4 — avance del wizard y referencia */}
                        <td>
                          <div className='d-flex flex-column align-items-start gap-1'>
                            <span className='badge badge-light-dark fs-8'>{progressLabel}</span>
                            {app.referenceNumber && (
                              <span className='text-gray-500 fs-8'>{app.referenceNumber}</span>
                            )}
                          </div>
                        </td>

                        {/* 5 — estado de revisión */}
                        <td>
                          {canChangeStatus ? (
                            <select
                              className={`form-select form-select-sm w-auto border-${
                                REVIEW_TONE[app.reviewStatus]
                              }`}
                              value={app.reviewStatus}
                              disabled={updating}
                              onChange={(e) =>
                                handleStatusChange(app.id, e.target.value as ReviewStatus)
                              }
                            >
                              {REVIEW_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                  {reviewLabel(s)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className={`badge badge-light-${REVIEW_TONE[app.reviewStatus]}`}>
                              {reviewLabel(app.reviewStatus)}
                            </span>
                          )}
                        </td>

                        {/* 6 — cuenta del CRM */}
                        <td>
                          {app.createdUser ? (
                            <span className='badge badge-light-success fs-8'>
                              <i className='bi bi-person-check me-1' />
                              {app.createdUser.email}
                            </span>
                          ) : canChangeStatus &&
                            (app.reviewStatus === 'APPROVED' ||
                              app.reviewStatus === 'ACTIVATED') ? (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={() => setUserFor(app)}
                            >
                              <i className='bi bi-person-plus me-1' />
                              {intl.formatMessage(M.createUser)}
                            </button>
                          ) : (
                            <span className='text-gray-400 fs-8'>{dash}</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  )
}
